import React, { useState, useEffect, useRef, useContext } from "react";
import { Button, Spinner, Modal, Row, FormGroup } from "react-bootstrap";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import Swal from "sweetalert2";
import {
  IconPlus,
  IconEdit,
  IconTrash,
  IconCheck,
  IconClock,
  IconCalendarEvent,
  IconPalette,
  IconToggleLeft,
  IconToggleRight,
  IconSearch,
  IconSettings,
  IconAlertCircle,
} from "@tabler/icons-react";
import { SketchPicker } from "react-color";
import {
  APPOINTMENT_TYPES_URL,
  BUSINESS_HOURS_SETTINGS_URL,
} from "../../api/Urls";
import { api } from "../../api/Middleware";
import NumberFormat from "react-number-format";
import UserContext from "../../contexts/UserContext";
import {
  ActionButton,
  ActionsContainer,
  CardContent,
  CardFooter,
  CardHeader,
  CardsContainer,
  ColorButton,
  ColorInputContainer,
  ColorPickerContainer,
  ColorSample,
  Container,
  CustomSwitch,
  Description,
  DurationInfo,
  EmptyIcon,
  EmptyState,
  ErrorMessage,
  FloatingButton,
  Header,
  HelpText,
  InfoCard,
  Input,
  Label,
  LoadingContainer,
  SearchContainer,
  SearchIcon,
  SearchInput,
  StatCard,
  StatLabel,
  StatNumber,
  StatsContainer,
  StatusBadge,
  SwitchContainer,
  TextArea,
  Title,
  TitleIcon,
  TypeCard,
  TypeName,
} from "./styles";

// Schema para validação dos tipos de atendimento
const appointmentTypeSchema = yup.object().shape({
  name: yup.string().required("Nome é obrigatório"),
  duration: yup
    .number()
    .required("Duração é obrigatória")
    .positive("Duração deve ser um número positivo")
    .integer("Duração deve ser um número inteiro"),
  description: yup.string(),
  price: yup.string(),
  color: yup.string().required("Cor é obrigatória"),
  is_active: yup.boolean().default(true),
});

// Schema para validação das configurações globais
const settingsSchema = yup.object().shape({
  appointment_duration: yup
    .number()
    .required("Duração padrão é obrigatória")
    .positive("Duração deve ser um número positivo")
    .integer("Duração deve ser um número inteiro"),
  appointment_interval: yup
    .number()
    .required("Intervalo é obrigatório")
    .min(0, "Intervalo deve ser zero ou positivo")
    .integer("Intervalo deve ser um número inteiro"),
});

const AppointmentTypesManager = () => {
  const { user } = useContext(UserContext);
  const [loading, setLoading] = useState(false);
  const [appointmentTypes, setAppointmentTypes] = useState([]);
  const [filteredTypes, setFilteredTypes] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [settings, setSettings] = useState(null);
  const pickerRef = useRef(null);

  // Form para tipo de atendimento
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    control,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(appointmentTypeSchema),
    defaultValues: {
      name: "",
      duration: 30,
      description: "",
      price: "",
      color: "#305cde",
      is_active: true,
    },
  });

  // Form para configurações globais
  const {
    register: registerSettings,
    handleSubmit: handleSubmitSettings,
    reset: resetSettings,
    formState: { errors: errorsSettings },
  } = useForm({
    resolver: yupResolver(settingsSchema),
    defaultValues: {
      appointment_duration: 30,
      appointment_interval: 0,
    },
  });

  const watchedColor = watch("color");
  const watchedIsActive = watch("is_active");

  // Carregar dados iniciais
  useEffect(() => {
    fetchAppointmentTypes();
    fetchSettings();
  }, []);

  // Detecta cliques fora do picker
  useEffect(() => {
    function handleClickOutside(event) {
      if (pickerRef.current && !pickerRef.current.contains(event.target)) {
        setShowColorPicker(false);
      }
    }

    if (showColorPicker) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showColorPicker]);

  // Filter types based on search term
  useEffect(() => {
    if (searchTerm.trim() === "") {
      setFilteredTypes(appointmentTypes);
    } else {
      const filtered = appointmentTypes.filter(
        (type) =>
          type.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (type.description &&
            type.description.toLowerCase().includes(searchTerm.toLowerCase()))
      );
      setFilteredTypes(filtered);
    }
  }, [searchTerm, appointmentTypes]);

  // Buscar configurações globais
  const fetchSettings = async () => {
    try {
      const response = await api.get(BUSINESS_HOURS_SETTINGS_URL, {
        params: { account: user?.account_id },
      });

      const settingsData =
        response.data.results?.[0] || response.data[0] || null;
      setSettings(settingsData);

      if (settingsData) {
        resetSettings({
          appointment_duration: settingsData.appointment_duration,
          appointment_interval: settingsData.appointment_interval || 0,
        });
      }
    } catch (error) {
      console.error("Erro ao carregar configurações:", error);
      // Não mostrar erro se for 404 (configurações não existem ainda)
      if (error.response?.status !== 404) {
        console.warn("Configurações não encontradas, usando valores padrão");
      }
    }
  };

  // Buscar tipos de atendimento
  const fetchAppointmentTypes = async () => {
    setLoading(true);
    try {
      const response = await api.get(APPOINTMENT_TYPES_URL);
      const types = response.data.results || response.data || [];
      setAppointmentTypes(types);
      setFilteredTypes(types);
    } catch (error) {
      console.error("Erro ao carregar tipos de atendimento:", error);
      Swal.fire({
        title: "Erro",
        text: "Não foi possível carregar os tipos de atendimento",
        icon: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  // Salvar configurações globais
  const onSubmitSettings = async (data) => {
    setLoading(true);
    try {
      const payload = {
        ...data,
        account: user?.account_id,
      };

      if (settings?.id) {
        // Atualizar configurações existentes
        await api.put(`${BUSINESS_HOURS_SETTINGS_URL}${settings.id}/`, payload);
      } else {
        // Criar novas configurações
        await api.post(BUSINESS_HOURS_SETTINGS_URL, payload);
      }

      Swal.fire({
        title: "Sucesso",
        text: "Configurações salvas com sucesso",
        icon: "success",
      });

      setShowSettingsModal(false);
      fetchSettings();
    } catch (error) {
      console.error("Erro ao salvar configurações:", error);
      Swal.fire({
        title: "Erro",
        text: error.response?.data?.detail || "Erro ao salvar configurações",
        icon: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  // Abrir modal de configurações
  const handleOpenSettings = () => {
    if (settings) {
      resetSettings({
        appointment_duration: settings.appointment_duration,
        appointment_interval: settings.appointment_interval || 0,
      });
    }
    setShowSettingsModal(true);
  };

  // Abrir modal para adicionar
  const handleAddNew = () => {
    reset({
      name: "",
      duration: settings?.appointment_duration || 30,
      description: "",
      price: "",
      color: "#305cde",
      is_active: true,
    });
    setEditingId(null);
    setShowModal(true);
  };

  // Abrir modal para editar
  const handleEdit = (type, e) => {
    e.stopPropagation();
    reset({
      name: type.name,
      duration: type.duration,
      description: type.description || "",
      price: type.price ? parseFloat(type.price) : "",
      color: type.color || "#305cde",
      is_active: type.is_active,
    });
    setEditingId(type.id);
    setShowModal(true);
  };

  // Salvar tipo de atendimento
  const onSubmit = async (data) => {
    setLoading(true);
    try {
      const payload = {
        ...data,
        account: user?.account_id,
      };

      if (editingId) {
        await api.put(`${APPOINTMENT_TYPES_URL}${editingId}/`, payload);
        Swal.fire({
          title: "Sucesso",
          text: "Tipo de atendimento atualizado com sucesso",
          icon: "success",
        });
      } else {
        await api.post(APPOINTMENT_TYPES_URL, payload);
        Swal.fire({
          title: "Sucesso",
          text: "Tipo de atendimento adicionado com sucesso",
          icon: "success",
        });
      }

      setShowModal(false);
      fetchAppointmentTypes();
    } catch (error) {
      console.error("Erro ao salvar tipo de atendimento:", error);
      Swal.fire({
        title: "Erro",
        text:
          error.response?.data?.detail || "Erro ao salvar tipo de atendimento",
        icon: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  // Remover tipo de atendimento
  const handleDelete = async (id, e) => {
    e.stopPropagation();
    const result = await Swal.fire({
      title: "Confirmar exclusão",
      text: "Tem certeza que deseja excluir este tipo de atendimento?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sim, excluir",
      cancelButtonText: "Cancelar",
    });

    if (result.isConfirmed) {
      setLoading(true);
      try {
        await api.delete(`${APPOINTMENT_TYPES_URL}${id}/`);
        Swal.fire({
          title: "Sucesso",
          text: "Tipo de atendimento removido com sucesso",
          icon: "success",
        });
        fetchAppointmentTypes();
      } catch (error) {
        console.error("Erro ao remover tipo de atendimento:", error);
        Swal.fire({
          title: "Erro",
          text: "Não foi possível remover o tipo de atendimento",
          icon: "error",
        });
      } finally {
        setLoading(false);
      }
    }
  };

  // Alternar status ativo/inativo
  const toggleActive = async (id, currentStatus, e) => {
    e.stopPropagation();
    setLoading(true);
    try {
      await api.post(`${APPOINTMENT_TYPES_URL}${id}/toggle_active/`);
      fetchAppointmentTypes();
    } catch (error) {
      console.error("Erro ao alterar status:", error);
      Swal.fire({
        title: "Erro",
        text: "Não foi possível alterar o status",
        icon: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const getStats = () => {
    const total = appointmentTypes.length;
    const active = appointmentTypes.filter((t) => t.is_active).length;
    const inactive = total - active;
    const avgDuration =
      total > 0
        ? Math.round(
            appointmentTypes.reduce((acc, t) => acc + t.duration, 0) / total
          )
        : 0;

    return { total, active, inactive, avgDuration };
  };

  const stats = getStats();

  if (loading && appointmentTypes.length === 0) {
    return (
      <Container>
        <LoadingContainer>
          <Spinner animation="border" variant="success" />
          <p className="mt-3">Carregando tipos de atendimento...</p>
        </LoadingContainer>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <Title>
          <TitleIcon>
            <IconCalendarEvent size={24} />
          </TitleIcon>
          Tipos de Atendimento
        </Title>
        <Button variant="outline-primary" onClick={handleOpenSettings}>
          <IconSettings size={18} className="mr-1"/>
          <span className="d-none d-md-inline">Configurações Globais</span>
          <span className="d-md-none">Config</span>
        </Button>
      </Header>

      {/* Informações sobre configurações atuais */}
      {settings && (
        <InfoCard
          style={{
            background: "linear-gradient(135deg, #e8f5e8, #f8f9fa)",
            borderLeft: "4px solid #28a745",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: "12px",
            }}
          >
            <p style={{ margin: 0, flex: 1 }}>
              <strong>Configurações atuais:</strong> Duração padrão de{" "}
              {settings.appointment_duration} minutos
              {settings.appointment_interval > 0 &&
                ` com ${settings.appointment_interval} minutos de intervalo entre atendimentos`}
            </p>
            <Button
              variant="link"
              size="sm"
              onClick={handleOpenSettings}
              style={{
                padding: "4px 8px",
                textDecoration: "none",
                fontWeight: "600",
              }}
            >
              <IconEdit size={14} className="me-1" />
              Alterar
            </Button>
          </div>
        </InfoCard>
      )}

      <InfoCard>
        <p>
          Configure os tipos de atendimento disponíveis e suas respectivas
          durações. Cada tipo pode ter uma duração específica em minutos e uma
          cor para identificação visual no calendário.
        </p>
      </InfoCard>

      <SearchContainer>
        <SearchIcon>
          <IconSearch size={18} />
        </SearchIcon>
        <SearchInput
          type="text"
          placeholder="Buscar tipos de atendimento..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </SearchContainer>

      <StatsContainer>
        <StatCard>
          <StatNumber>{stats.total}</StatNumber>
          <StatLabel>Total</StatLabel>
        </StatCard>
        <StatCard>
          <StatNumber>{stats.active}</StatNumber>
          <StatLabel>Ativos</StatLabel>
        </StatCard>
        <StatCard>
          <StatNumber>{stats.inactive}</StatNumber>
          <StatLabel>Inativos</StatLabel>
        </StatCard>
        <StatCard>
          <StatNumber>{stats.avgDuration}min</StatNumber>
          <StatLabel>Duração Média</StatLabel>
        </StatCard>
      </StatsContainer>

      {filteredTypes.length === 0 ? (
        <EmptyState>
          <EmptyIcon>📅</EmptyIcon>
          <h3>
            {searchTerm ? "Nenhum tipo encontrado" : "Nenhum tipo configurado"}
          </h3>
          <p>
            {searchTerm
              ? "Tente ajustar sua busca para encontrar tipos de atendimento."
              : "Comece criando seus primeiros tipos de atendimento para organizar melhor seus agendamentos."}
          </p>
          {!searchTerm && (
            <Button variant="primary" onClick={handleAddNew}>
              Criar Primeiro Tipo
            </Button>
          )}
        </EmptyState>
      ) : (
        <CardsContainer>
          {filteredTypes.map((type) => (
            <TypeCard key={type.id} color={type.color}>
              <CardHeader>
                <TypeName>{type.name}</TypeName>
                <StatusBadge active={type.is_active}>
                  {type.is_active ? "Ativo" : "Inativo"}
                </StatusBadge>
              </CardHeader>

              <CardContent>
                <DurationInfo>
                  <IconClock size={16} />
                  {type.duration} minutos
                </DurationInfo>

                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    marginBottom: "12px",
                  }}
                >
                  <ColorSample color={type.color} />
                  <span style={{ fontSize: "14px", color: "#6c757d" }}>
                    Cor de identificação
                  </span>
                </div>

                {type.description && (
                  <Description>{type.description}</Description>
                )}
              </CardContent>

              <CardFooter>
                <div></div>
                <ActionsContainer>
                  <ActionButton
                    variant={type.is_active ? "toggle-on" : "toggle-off"}
                    onClick={(e) => toggleActive(type.id, type.is_active, e)}
                    title={type.is_active ? "Desativar" : "Ativar"}
                  >
                    {type.is_active ? (
                      <IconToggleRight size={16} />
                    ) : (
                      <IconToggleLeft size={16} />
                    )}
                  </ActionButton>

                  <ActionButton
                    variant="edit"
                    onClick={(e) => handleEdit(type, e)}
                    title="Editar"
                  >
                    <IconEdit size={16} />
                  </ActionButton>

                  <ActionButton
                    variant="delete"
                    onClick={(e) => handleDelete(type.id, e)}
                    title="Excluir"
                  >
                    <IconTrash size={16} />
                  </ActionButton>
                </ActionsContainer>
              </CardFooter>
            </TypeCard>
          ))}
        </CardsContainer>
      )}

      <FloatingButton onClick={handleAddNew} title="Novo Tipo de Atendimento">
        <IconPlus size={24} />
      </FloatingButton>

      {/* Modal de configurações globais */}
      <Modal
        show={showSettingsModal}
        onHide={() => setShowSettingsModal(false)}
        size="md"
        centered
      >
        <form onSubmit={handleSubmitSettings(onSubmitSettings)}>
          <Modal.Header closeButton>
            <Modal.Title>
              <IconSettings size={20} className="me-2" />
              Configurações Globais
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div
              style={{
                background: "#f8f9fa",
                padding: "16px",
                borderRadius: "8px",
                marginBottom: "20px",
                border: "1px solid #e9ecef",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  marginBottom: "8px",
                }}
              >
                <IconAlertCircle size={16} color="#17a2b8" />
                <strong style={{ color: "#17a2b8", fontSize: "14px" }}>
                  Informação Importante
                </strong>
              </div>
              <p
                style={{
                  margin: 0,
                  fontSize: "14px",
                  color: "#495057",
                  lineHeight: "1.4",
                }}
              >
                Essas configurações servem como referência para os slots de tempo da agenda.
              </p>
            </div>

            <FormGroup>
              <Label>
                Duração Padrão (minutos){" "}
                <span style={{ color: "#dc3545" }}>*</span>
              </Label>
              <Input
                type="number"
                min="1"
                placeholder="30"
                {...registerSettings("appointment_duration")}
                className={
                  errorsSettings.appointment_duration ? "is-invalid" : ""
                }
              />
              {errorsSettings.appointment_duration && (
                <ErrorMessage>
                  {errorsSettings.appointment_duration.message}
                </ErrorMessage>
              )}
              <HelpText>
                Duração padrão em minutos para novos tipos de atendimento.
              </HelpText>
            </FormGroup>

            <FormGroup className="mt-2">
              <Label>
                Intervalo Entre Atendimentos (minutos){" "}
                <span style={{ color: "#dc3545" }}>*</span>
              </Label>
              <Input
                type="number"
                min="0"
                placeholder="0"
                {...registerSettings("appointment_interval")}
                className={
                  errorsSettings.appointment_interval ? "is-invalid" : ""
                }
              />
              {errorsSettings.appointment_interval && (
                <ErrorMessage>
                  {errorsSettings.appointment_interval.message}
                </ErrorMessage>
              )}
              <HelpText>
                Tempo adicional entre atendimentos. Use 0 para não ter
                intervalo.
              </HelpText>
            </FormGroup>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => setShowSettingsModal(false)}
            >
              Cancelar
            </Button>
            <Button variant="primary" type="submit" disabled={loading}>
              {loading ? (
                <Spinner animation="border" size="sm" className="me-1" />
              ) : (
                <IconCheck size={16} className="me-1" />
              )}
              Salvar Configurações
            </Button>
          </Modal.Footer>
        </form>
      </Modal>

      {/* Modal de adição/edição */}
      <Modal
        show={showModal}
        onHide={() => {
          setShowModal(false);
          setShowColorPicker(false);
        }}
        size="lg"
        centered
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <Modal.Header closeButton>
            <Modal.Title>
              <IconCalendarEvent size={20} className="me-2" />
              {editingId
                ? "Editar Tipo de Atendimento"
                : "Novo Tipo de Atendimento"}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <FormGroup>
              <Label>
                Nome <span style={{ color: "#dc3545" }}>*</span>
              </Label>
              <Input
                type="text"
                placeholder="Ex: Consulta Inicial"
                {...register("name")}
                className={errors.name ? "is-invalid" : ""}
              />
              {errors.name && (
                <ErrorMessage>{errors.name.message}</ErrorMessage>
              )}
            </FormGroup>
            <Row>
              <FormGroup className="col-md-6 col-sm-12">
                <Label>
                  Duração (minutos) <span style={{ color: "#dc3545" }}>*</span>
                </Label>
                <Input
                  type="number"
                  min="1"
                  {...register("duration")}
                  className={errors.duration ? "is-invalid" : ""}
                />
                {errors.duration && (
                  <ErrorMessage>{errors.duration.message}</ErrorMessage>
                )}
                <HelpText>
                  Tempo em minutos que este tipo de atendimento ocupa no
                  calendário.
                </HelpText>
              </FormGroup>
              <FormGroup className="col-md-6 col-sm-12">
                <Label>Valor</Label>
                <Controller
                  control={control}
                  name="price"
                  rules={{ required: true }}
                  render={({ field }) => (
                    <NumberFormat
                      fixedDecimalScale={true}
                      decimalScale={2}
                      thousandSeparator="."
                      decimalSeparator=","
                      prefix="R$"
                      className={`form-control ${
                        errors.duration ? "is-invalid" : ""
                      }`}
                      {...field}
                    />
                  )}
                />
                {errors.price && <ErrorMessage>{errors.price}</ErrorMessage>}
              </FormGroup>
            </Row>

            <FormGroup>
              <Label>Descrição (opcional)</Label>
              <TextArea
                rows={3}
                placeholder="Descrição deste tipo de atendimento"
                {...register("description")}
              />
            </FormGroup>

            <FormGroup>
              <Label>
                Cor <span style={{ color: "#dc3545" }}>*</span>
              </Label>
              <ColorInputContainer>
                <Input
                  type="text"
                  value={watchedColor}
                  onChange={(e) => setValue("color", e.target.value)}
                  className={errors.color ? "is-invalid" : ""}
                  style={{ flex: 1, width: "80%" }}
                />
                <ColorButton
                  type="button"
                  onClick={() => setShowColorPicker(!showColorPicker)}
                >
                  <div
                    style={{
                      backgroundColor: watchedColor,
                      width: "20px",
                      height: "20px",
                      borderRadius: "4px",
                    }}
                  />
                  <IconPalette size={16} />
                </ColorButton>
              </ColorInputContainer>
              {errors.color && (
                <ErrorMessage>{errors.color.message}</ErrorMessage>
              )}
              {showColorPicker && (
                <ColorPickerContainer ref={pickerRef}>
                  <SketchPicker
                    color={watchedColor}
                    onChange={(color) => setValue("color", color.hex)}
                  />
                </ColorPickerContainer>
              )}
            </FormGroup>

            <SwitchContainer>
              <CustomSwitch
                checked={watchedIsActive}
                onClick={() => setValue("is_active", !watchedIsActive)}
              />
              <Label>Ativo</Label>
            </SwitchContainer>
            <HelpText>
              Desative para ocultar temporariamente este tipo de atendimento.
            </HelpText>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => {
                setShowModal(false);
                setShowColorPicker(false);
              }}
            >
              Cancelar
            </Button>
            <Button variant="primary" type="submit" disabled={loading}>
              {loading ? (
                <Spinner animation="border" size="sm" className="me-1" />
              ) : (
                <IconCheck size={16} className="me-1" />
              )}
              {editingId ? "Atualizar" : "Adicionar"}
            </Button>
          </Modal.Footer>
        </form>
      </Modal>
    </Container>
  );
};

export default AppointmentTypesManager;
